import * as Sentry from '@sentry/react';

/**
 * Initialize Sentry error tracking
 *
 * To enable Sentry:
 * 1. Sign up for a Sentry account at https://sentry.io
 * 2. Create a new React project in Sentry
 * 3. Add REACT_APP_SENTRY_DSN to your .env file with the DSN from Sentry
 * 4. Optionally add REACT_APP_SENTRY_ENVIRONMENT (e.g., 'production', 'development', 'staging')
 */
export const initializeSentry = () => {
  const sentryDsn = process.env.REACT_APP_SENTRY_DSN;
  const environment = process.env.REACT_APP_SENTRY_ENVIRONMENT || process.env.NODE_ENV;

  // Only initialize if DSN is provided
  if (sentryDsn) {
    Sentry.init({
      dsn: sentryDsn,
      environment,

      // Set tracesSampleRate to 1.0 to capture 100% of transactions for performance monitoring.
      // We recommend adjusting this value in production
      tracesSampleRate: environment === 'production' ? 0.1 : 1.0,

      // Capture Replay for user session tracking
      // This can be helpful for debugging user issues
      integrations: [
        Sentry.replayIntegration({
          maskAllText: true, // Mask all text to protect privacy
          blockAllMedia: true, // Block all media to reduce data
        }),
      ],

      // Session Replay sample rate
      replaysSessionSampleRate: environment === 'production' ? 0.1 : 1.0,

      // If an error occurs, increase the sample rate
      replaysOnErrorSampleRate: 1.0,

      // Filter out common non-error events
      beforeSend(event, hint) {
        // Don't send events for navigation errors (these are usually user cancellations)
        if (event.exception?.values?.[0]?.type === 'NavigationDuplicated') {
          return null;
        }

        // Don't send events for AbortError (these are usually intentional cancellations)
        if (event.exception?.values?.[0]?.type === 'AbortError') {
          return null;
        }

        return event;
      },

      // Ignore specific errors
      ignoreErrors: [
        // Browser extensions
        'top.GLOBALS',
        'chrome-extension://',
        'moz-extension://',

        // Network errors that are expected
        'Network request failed',
        'NetworkError',
        'Failed to fetch',

        // ResizeObserver errors (benign)
        'ResizeObserver loop limit exceeded',
      ],
    });

    console.log('Sentry initialized in', environment, 'environment');
  }
  // Sentry is optional - silently skip if not configured
};

/**
 * Manually log an error to Sentry
 */
export const logError = (error: Error, context?: Record<string, any>) => {
  if (process.env.REACT_APP_SENTRY_DSN) {
    Sentry.captureException(error, { extra: context });
  } else {
    console.error('Error:', error, context);
  }
};

/**
 * Set user context for Sentry
 */
export const setUserContext = (user: { id: string; email?: string; username?: string }) => {
  if (process.env.REACT_APP_SENTRY_DSN) {
    Sentry.setUser(user);
  }
};

/**
 * Clear user context
 */
export const clearUserContext = () => {
  if (process.env.REACT_APP_SENTRY_DSN) {
    Sentry.setUser(null);
  }
};

/**
 * Add breadcrumb for debugging
 */
export const addBreadcrumb = (message: string, category: string, data?: Record<string, any>) => {
  if (process.env.REACT_APP_SENTRY_DSN) {
    Sentry.addBreadcrumb({
      message,
      category,
      data,
      level: 'info',
    });
  }
};
